import { t } from "@travily/i18n";
import type ListingsPhoto from "@travily/types/models/ListingsPhoto";
import { Icon } from "@ui/components/Icon";
import { SoftButton } from "@ui/components/SoftButton";
import { Wizard } from "@ui/components/Wizard";
import type { ReactNode } from "react";
import { PhotosField, photosFieldCopy } from "@/components/ListingFields/PhotosField";
import type { UploadFileItem } from "@/components/PhotoUploadModal/PhotoUploadModal";

const COLOR = "green" as const;
const LISTING_TYPE = "product" as const;

export interface ProductPhotosStepViewProps {
  currentStage: number;
  totalStages: number;
  onBack?: () => void;
  /** Wizard top-bar tertiary actions (Questions / Finish later). */
  topBarActions: ReactNode;
  onPrimary: () => void;
  primaryPending: boolean;
  photos: ListingsPhoto[];
  minPhotos: number;
  photosError?: string;
  /** Open the photo-upload modal (empty-state CTA, gallery header, "add more"). */
  onAddPhotos: () => void;
  onDeletePhoto: (photoId: string) => void;
  onReorderPhotos: (orderedIds: string[]) => void;
  isUploadModalOpen: boolean;
  uploadFiles: UploadFileItem[];
  isUploading: boolean;
  allUploadsCompleted: boolean;
  onAddFiles: (files: FileList) => void;
  onConfirmUpload: () => void;
  onUploadModalOpenChange: (isOpen: boolean) => void;
  onRemoveUploadFile: (id: string) => void;
  isDeleteModalOpen: boolean;
  onConfirmDelete: () => void;
  onDeleteModalOpenChange: (isOpen: boolean) => void;
}

export function ProductPhotosStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  photos,
  minPhotos,
  photosError,
  onAddPhotos,
  onDeletePhoto,
  onReorderPhotos,
  isUploadModalOpen,
  uploadFiles,
  isUploading,
  allUploadsCompleted,
  onAddFiles,
  onConfirmUpload,
  onUploadModalOpenChange,
  onRemoveUploadFile,
  isDeleteModalOpen,
  onConfirmDelete,
  onDeleteModalOpenChange,
}: ProductPhotosStepViewProps) {
  const hasPhotos = photos.length > 0;
  const { heading, helper } = photosFieldCopy({ hasPhotos, listingType: LISTING_TYPE, minPhotos });

  const field = (
    <PhotosField
      allUploadsCompleted={allUploadsCompleted}
      error={photosError}
      isDeleteModalOpen={isDeleteModalOpen}
      isUploadModalOpen={isUploadModalOpen}
      isUploading={isUploading}
      listingType={LISTING_TYPE}
      minPhotos={minPhotos}
      onAddFiles={onAddFiles}
      onAddPhotos={onAddPhotos}
      onConfirmDelete={onConfirmDelete}
      onConfirmUpload={onConfirmUpload}
      onDeleteModalOpenChange={onDeleteModalOpenChange}
      onDeletePhoto={onDeletePhoto}
      onRemoveUploadFile={onRemoveUploadFile}
      onReorderPhotos={onReorderPhotos}
      onUploadModalOpenChange={onUploadModalOpenChange}
      photos={photos}
      uploadFiles={uploadFiles}
    />
  );

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      {hasPhotos ? (
        <Wizard.EntitiesStage
          heading={heading}
          helper={helper}
          headerAction={
            <SoftButton aria-label={t("web.listings.fields.photos.add_more")} onPress={onAddPhotos} size="md">
              <Icon name="add" size="20" />
            </SoftButton>
          }
        >
          {field}
        </Wizard.EntitiesStage>
      ) : (
        <Wizard.Stage heading={heading} helper={helper}>
          {field}
        </Wizard.Stage>
      )}
    </Wizard>
  );
}
